import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TAB_BAR_MEDIA_QUERY, useOpenTabs, type TabSwitchState } from '@/components/openTabs';
import { useUiStore } from '@/store/uiStore';

/**
 * Ein normaler Linksklick ohne Modifier — den führt der Router im selben Tab aus.
 * Cmd/Strg/Umschalt/Alt gehören dem Browser (neuer Tab/neues Fenster).
 */
export function isPlainLeftClick(event: React.MouseEvent): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

/**
 * Ziel der Palette öffnen: im aktuellen Tab — oder, wenn die Suche über „+" der
 * Tab-Leiste kam, in einem neuen (ADR 0111). Beim normalen Klick auf einen
 * Treffer-Anker übernimmt `openFromClick` das, statt den Router im selben Tab
 * springen zu lassen.
 *
 * `openInBackground` (⌘/Strg+↵ in der Palette): Tab hinter dem aktuellen, ohne
 * hinzuwechseln — wie ⌘-Klick auf einen Link. Ohne sichtbare Tab-Leiste (Handy)
 * gäbe es den Tab nur unsichtbar; dort öffnet es normal und meldet `false`.
 */
export function usePaletteNavigate() {
  const navigate = useNavigate();
  const opensNewTab = useUiStore((state) => state.searchOpensNewTab);
  const go = useCallback(
    (to: string) => {
      if (!opensNewTab) {
        navigate(to);
        return;
      }
      const tabId = useOpenTabs.getState().prepareTab(to);
      navigate(to, { state: { tabSwitch: tabId } satisfies TabSwitchState });
    },
    [navigate, opensNewTab],
  );
  const openFromClick = useCallback(
    (event: React.MouseEvent, to: string) => {
      if (!opensNewTab) return;
      event.preventDefault();
      go(to);
    },
    [go, opensNewTab],
  );
  const openInBackground = useCallback(
    (to: string): boolean => {
      const hasTabBar = typeof window.matchMedia === 'function' && window.matchMedia(TAB_BAR_MEDIA_QUERY).matches;
      if (!hasTabBar || !useOpenTabs.getState().userId) {
        go(to);
        return false;
      }
      useOpenTabs.getState().openInBackground(to);
      return true;
    },
    [go],
  );
  return { go, openFromClick, openInBackground, opensNewTab };
}
